import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useGetTasksForSuggestedPlaylistQuery,
  useGetSuggestedPlaylistByIdQuery,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.api';
import {
  selectCurrentPlaylistId,
  openPlaylistViewStep,
  closePlaylistDrawer,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';

import { Box, Button, Chip, Skeleton, SxProps, Theme, Typography } from '@mui/material';

import { TaskCard } from './TaskCard';
import {
  PLAYLIST_CARD_DRAWER_WIDTH,
  DRAWER_MAX_WIDTH,
  PLAYLIST_SKELETON_HEIGHT,
} from '../constants/priorities.constants';
import { useAdoptSuggestedPlaybook, usePlaylistTasksSort } from '../hooks/playlists.hooks';

const PLAYLIST_TEMPLATE_DRAWER_STRINGS = appStrings.modules.priorities.playlistTemplateDrawer;
const PLAYLIST_CARD_STRINGS = appStrings.modules.priorities.playlistCard;

type TemplateViewDrawerProps = Readonly<{
  handleClose: () => void;
}>;

export const TemplateViewDrawer = ({ handleClose }: TemplateViewDrawerProps) => {
  const dispatch = useAppDispatch();
  const currentTemplatePlaylistId = useAppSelector(selectCurrentPlaylistId);

  const { data: currentSuggestedPlaylist } = useGetSuggestedPlaylistByIdQuery(
    currentTemplatePlaylistId || 0,
    {
      skip: !currentTemplatePlaylistId,
    },
  );

  const { data: tasksForPlaylist, isLoading: isLoadingTasksForPlaylist } =
    useGetTasksForSuggestedPlaylistQuery(currentTemplatePlaylistId || 0, {
      skip: !currentTemplatePlaylistId,
    });

  const { adopt, isAdding: isLoading } = useAdoptSuggestedPlaybook();

  const { sortedTasks } = usePlaylistTasksSort(tasksForPlaylist);

  const handleAddPlaylist = async () => {
    if (!currentSuggestedPlaylist) return;

    try {
      const usersPlaylist = await adopt(currentSuggestedPlaylist);

      dispatch(closePlaylistDrawer());

      if (currentSuggestedPlaylist.is_draft !== true) {
        dispatch(openPlaylistViewStep(usersPlaylist));
      }
    } catch (error) {
      console.error('Failed to add suggested playlist:', error);
    }
  };

  if (!currentSuggestedPlaylist) {
    return null;
  }

  return (
    <>
      <Box sx={headerSx}>
        <Box component="img" src={currentSuggestedPlaylist.preview_media?.media_url} sx={imageSx} />

        <Box sx={titleContainerSx}>
          <Box sx={titleAndDescriptionContainerSx}>
            <Typography variant="text-xl-semibold" sx={titleSx}>
              {currentSuggestedPlaylist.title}
            </Typography>
            <Typography variant="text-sm-regular" sx={descriptionSx}>
              {currentSuggestedPlaylist.description}
            </Typography>
          </Box>
          <Chip
            variant="pill"
            size="sm"
            label={PLAYLIST_CARD_STRINGS.tasksCount(currentSuggestedPlaylist.total_tasks)}
            sx={taskCountChipSx}
          />
        </Box>

        <Box onClick={handleClose} sx={closeButtonSx}>
          <AppIcon iconName="XClose" diameter="20px" color="Colors/Foreground/fg-quinary" />
        </Box>
      </Box>

      {/* ADD PLAYLIST BUTTON */}
      <Box sx={taskButtonAndSortContainerSx}>
        <Button
          id="template-add-playlist-button"
          variant="contained"
          hierarchy="Primary"
          size="md"
          fullWidth
          onClick={handleAddPlaylist}
          disabled={isLoading}
          startIcon={<AppIcon iconName="Plus" diameter="20px" color="Colors/Base/White" />}
        >
          <Typography variant="text-sm-semibold" color="Colors/Base/White">
            {isLoading
              ? PLAYLIST_TEMPLATE_DRAWER_STRINGS.addingPlaylist
              : PLAYLIST_TEMPLATE_DRAWER_STRINGS.addPlaylist}
          </Typography>
        </Button>
      </Box>

      <Box sx={playlistsContainerSx}>
        {isLoadingTasksForPlaylist && (
          <>
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                height={PLAYLIST_SKELETON_HEIGHT}
                width={PLAYLIST_CARD_DRAWER_WIDTH}
                animation="pulse"
              />
            ))}
          </>
        )}

        {!isLoadingTasksForPlaylist &&
          sortedTasks?.map((task) => (
            <Box key={task.id} sx={{ opacity: 0.76 }}>
              <TaskCard key={task.id} task={task} restrictWidth={false} readOnly />
            </Box>
          ))}
      </Box>
    </>
  );
};

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  width: '100%',
  gap: backrs.spacing['spacing-lg'],
  marginBottom: backrs.spacing['spacing-3xl'],
  px: backrs.spacing['spacing-sm'],
  pt: backrs.spacing['spacing-3xl'],
  position: 'relative',
});

const taskCountChipSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  backgroundColor: backrs.colors['Colors/Background/bg-active'],
});

const imageSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: '100%',
  borderRadius: backrs.spacing['spacing-xs'],
  objectFit: 'cover',
  overflow: 'hidden',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,

  maxWidth: '376px',
  maxHeight: '222px',
});

const closeButtonSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  marginLeft: 'auto',
  position: 'absolute',
  top: backrs.spacing['spacing-5xl'],
  right: backrs.spacing['spacing-2xl'],
});

const playlistsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  pt: backrs.spacing['spacing-xs'],
  minWidth: {
    xs: `calc(100vw - (${backrs.spacing['spacing-4xl']}))`,
    sm: `calc(${DRAWER_MAX_WIDTH}px - (${backrs.spacing['spacing-2xl']}))`,
  },
  px: backrs.spacing['spacing-sm'],
  pb: backrs.spacing['spacing-xl'],
  overflowY: 'auto',
  flex: 1,
  minHeight: 0,
});

const titleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const titleAndDescriptionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  maxWidth: { sm: `calc(${PLAYLIST_CARD_DRAWER_WIDTH}px - (${backrs.spacing['spacing-7xl']}))` }, // to account for the padding of the container
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: { sm: `calc(${PLAYLIST_CARD_DRAWER_WIDTH}px - (${backrs.spacing['spacing-7xl']}))` }, // to account for the padding of the container
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  display: '-webkit-box',
  WebkitLineClamp: 5,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: { sm: `calc(${PLAYLIST_CARD_DRAWER_WIDTH}px - (${backrs.spacing['spacing-7xl']}))` }, // to account for the padding of the container
});

const taskButtonAndSortContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  px: `calc(${backrs.spacing['spacing-2xl']} + ${backrs.spacing['spacing-xxs']})`, // to account for negative margin for border
  pb: backrs.spacing['spacing-xl'],
  mb: backrs.spacing['spacing-xl'],
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  mx: `-${backrs.spacing['spacing-xl']}`,
});
